import { z } from 'zod/v4';
import { CAPA_FINDING_STATUS } from '../../constants.js';

/**
 * One entity tag as a finding reports it — id, label and swatch, nothing else.
 *
 * Deliberately narrower than the full `entity_tags` row: the findings reads
 * aggregate tags inline in SQL and have never projected the audit columns.
 */
export const FindingTagSchema = z.object({
  id: z.string(),
  title: z.string(),
  colorCode: z.string(),
});

export type FindingTag = z.infer<typeof FindingTagSchema>;

const findingStatusValues = Object.values(CAPA_FINDING_STATUS) as [
  string,
  ...string[],
];

/**
 * A finding whose `type` is `AUDIT`, resolved through `audits_service_audits`.
 */
const FindingSourceAuditSchema = z.object({
  id: z.string(),
  name: z.string(),
  auditTypeId: z.string().nullable(),
  auditTypeName: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  auditDate: z.union([z.string(), z.date()]).nullable(),
  facilityScore: z.string().nullable(),
});

/**
 * A finding whose `type` is `SLCP`, resolved through
 * `audits_service_slcp_assessments`.
 */
const FindingSourceSlcpSchema = z.object({
  id: z.string(),
  objectId: z.string(),
  version: z.string(),
  status: z.string(),
  revision: z.string(),
  submittedOn: z.union([z.string(), z.date()]).nullable(),
});

/**
 * What the finding was raised against.
 *
 * Three shapes, in match order: the audit branch, the SLCP branch, and `{}` for
 * every other `type` (`FACTORY_VISIT`, `OTHER`) **and** for a source the reading
 * company cannot reach — the network read only resolves an audit it has been
 * granted and an assessment shared with it, so an unreachable source degrades to
 * an empty object rather than leaking the audit's name.
 *
 * The date fields accept a string or a `Date` because the two reads produce
 * different things: the list SQL hands back the raw timestamp, the detail SQL
 * `TO_CHAR`s it. Both serialise to the same ISO string on the wire.
 */
export const FindingSourceObjectSchema = z.union([
  FindingSourceAuditSchema,
  FindingSourceSlcpSchema,
  z.looseObject({}),
]);

export type FindingSourceObject = z.infer<typeof FindingSourceObjectSchema>;

/**
 * One audit finding as the CAPA findings **lists** report it — the shared
 * resource for `GET /api/capa/findings/my-company`, its `/archive` twin, and the
 * two `network` counterparts.
 *
 * Notes on the shape, all of them load-bearing:
 *
 * - `status` is **derived**, never a stored column: a finding is DONE when every
 *   action the reading company can see against it is DONE, TO_DO when they all
 *   are (or there are none), IN_PROGRESS for any other mix. Two companies see
 *   different statuses for the same finding, and `filter.status` is applied to
 *   the derived value in an outer query rather than in the WHERE clause.
 * - `actionCount` and `planCount` are likewise scoped to the reading company and
 *   count only non-`CAP` actions; `planCount` counts the *plans* those actions
 *   hang off. The detail read defines both differently — see `FindingDetail`.
 * - `additionalInformation` and `suggestedActions` are raw jsonb **strings**, not
 *   parsed objects. The SLCP consumers parse `additionalInformation` themselves.
 * - `isLinkedToPlan` is present only when the request passed `capId`; it drives
 *   the "already on this plan" ordering in the finding picker.
 * - `rootCauseAnalysis` and `genderedImplications` are deliberately **absent**:
 *   DELTA-2311 restricted, and the lists have never served them. The CAP export
 *   reads them straight off the service, which bypasses this schema — they live
 *   on `FindingListRow`. `assigneeUserName` used to sit with them and is on the
 *   contract since the public API published the assignee by name; every read
 *   computes it, null when the finding is unassigned.
 */
export const FindingResourceSchema = z.object({
  id: z.string(),
  auditId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  urgency: z.string().nullable(),
  source: z.string().nullable(),
  impactAreaId: z.string().nullable(),
  suggestedActions: z.string().nullable(),
  additionalInformation: z.string().nullable(),
  type: z.string().nullable(),
  companyId: z.string(),
  /** The responsible company's display name, from its `ABOUT` profile. */
  companyName: z.string().nullable().optional(),
  isRemoved: z.number(),
  dueDate: z.date().nullable(),
  status: z.enum(findingStatusValues),
  assigneeUserId: z.string().nullable(),
  assigneeUserName: z.string().nullable(),
  externalSource: z.string().nullable(),
  createdAt: z.date().nullable().optional(),
  createdByUserId: z.string().nullable().optional(),
  createdByUserName: z.string().nullable().optional(),
  updatedAt: z.date().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  createdByImpersonateId: z.string().nullable().optional(),
  createdByCompanyId: z.string().nullable().optional(),
  updatedByImpersonateId: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByImpersonateName: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  createdByApiKeyId: z.string().nullable().optional(),
  createdByApiKeyLabel: z.string().nullable().optional(),
  updatedByApiKeyId: z.string().nullable().optional(),
  updatedByApiKeyLabel: z.string().nullable().optional(),
  actionCount: z.number(),
  planCount: z.number(),
  tags: z.array(FindingTagSchema),
  sourceObject: FindingSourceObjectSchema.optional(),
  isLinkedToPlan: z.number().optional(),
});

export type FindingResource = z.infer<typeof FindingResourceSchema>;
